import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ISurveyPlugin,
  PluginContexts,
  SurveyPluginUsage,
} from '@hyper-inferno/survey-elements-core';
import { PluginUsageDetail } from '../plugin-usage-detail/plugin-usage-detail';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { SurveyPluginService } from '../survey-plugin-service/survey-plugin.service';

@Component({
  selector: 'lib-setting-type-plugin-detail',
  imports: [CommonModule, PluginUsageDetail],
  templateUrl: './setting-type-plugin-detail.html',
  styleUrl: './setting-type-plugin-detail.css',
})
export class SettingTypePluginDetail {
  surveyPluginService = inject(SurveyPluginService);
  pluginUsages = signal<SurveyPluginUsage[]>([]);
  activePluginUsageId = signal<string | number | null>(null);
  surveyPlugins = signal<ISurveyPlugin[]>([]);
  elementId = input.required<number>();
  private readonly route = inject(ActivatedRoute);
  constructor() {
    // Effects automatically re-run whenever any signal inside them changes
    effect(() => {
      const id = this.elementId();
      if (id) {
        this.getElementPluginUsages();
        this.getPlugins();
      }
    });
  }
  setActiveTab(usageId: string) {
    this.activePluginUsageId.set(usageId);
  }
  pluginName = computed(() => {
    const pluginNameMap = new Map<string, string>();
    for (const plugin of this.surveyPlugins()) {
      pluginNameMap.set(plugin.id, plugin.name);
    }
    return pluginNameMap;
  });
  getPluginDisplayName(pluginId: string | null): string {
    if (pluginId) {
      return this.pluginName().get(pluginId) || 'Unknown Plugin';
    }
    return '';
  }
  async getElementPluginUsages() {
    const surveyId = this.route.snapshot.paramMap.get('id');
    try {
      const rawData = await lastValueFrom(
        this.surveyPluginService.getSurveyPluginUsages(Number(surveyId)),
      );

      const filteredData = rawData.filter(
        (plugin) =>
          plugin.plugin_type === PluginContexts.Element &&
          Number(plugin.element_id) === Number(this.elementId()),
      );
      this.pluginUsages.set(filteredData);
      // Autoselect the first tab
      if (filteredData.length > 0) {
        this.setActiveTab(filteredData[0].id);
      } else {
        this.activePluginUsageId.set(null);
      }
    } catch (error) {
      console.error('Error fetching plugin usages:', error);
    }
  }
  async getPlugins() {
    try {
      const pluginsList = await lastValueFrom(
        this.surveyPluginService.getSurveyPlugins(),
      );
      this.surveyPlugins.set(pluginsList);
    } catch (error) {
      console.error(`Error loading plugins`, error);
    }
  }
}
